import {
  type InvestmentActivityTrade,
  InvestmentAmountType,
  InvestmentTradeSide,
  useInvestmentAccountActivity,
} from '@egw/api'
import { Button, ButtonVariant, Link, PageHeader } from '@egw/components'
import { DEFAULT_CURRENCY_CODE, WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { Currency, getCurrencySymbol } from '@egw/utils'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import cx from 'classnames'
import { type FC, useCallback } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useNavigate, useParams } from 'react-router'
import { TradeField } from '../../../components/trade-field'

export const InvestmentsTrades: FC = () => {
  const { t } = useTranslation('features-transactions')
  const navigate = useNavigate()

  const { alias = '', transactionId = '' } = useParams<{ alias: string; transactionId: string }>()

  const {
    data: investmentAccountActivity,
    isLoading,
    isError,
  } = useInvestmentAccountActivity({ alias: parseInt(alias) }, { enabled: Boolean(alias && transactionId) })

  const transaction = investmentAccountActivity?.activity.find((item) => item.activityId.toString() === transactionId)

  const trades =
    transaction?.__typename === 'InvestmentTransferActivityDetailV2' ||
    transaction?.__typename === 'InvestmentFeeActivityDetail' ||
    transaction?.__typename === 'InvestmentInterestActivityDetail' ||
    transaction?.__typename === 'InvestmentDividendActivityDetailV2'
      ? transaction.trades
      : []

  const getValue = useCallback(
    (trade: InvestmentActivityTrade): JSX.Element => {
      if (trade.amount && trade.amountType === InvestmentAmountType.NOTIONAL) {
        return (
          <>
            <span>{getCurrencySymbol(DEFAULT_CURRENCY_CODE)}</span>
            <span>{Currency.integerCents.onlyMinus(Number(trade.amount))}</span>
          </>
        )
      }
      return <span>{t('activity.transaction_details.pending')}</span>
    },
    [t],
  )

  const getLabel = useCallback(
    (trade: InvestmentActivityTrade): string => {
      return trade.side === InvestmentTradeSide.BUY
        ? t('activity.transaction_details.bought', { symbol: trade.symbol })
        : t('activity.transaction_details.sell', { symbol: trade.symbol })
    },
    [t],
  )

  if (isLoading) {
    return (
      <div className="egw-container">
        <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
          <Skeleton count={1} height="100px" className="mb-xl" />
          <Skeleton count={5} height="50px" />
        </SkeletonTheme>
      </div>
    )
  }

  if (isError) {
    return (
      <>
        <PageHeader
          title={t('trades.trades_placed')}
          subtitle={t('activity.title')}
          backLinkUrl={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_TRADES.backPath
            ?.replace(':alias', alias.toString())
            .replace(':transactionId', transactionId.toString())}
        />
        <div className="egw-container">
          <InformationCircleIcon className="w-2xl h-2xl fill-red-500 mb-sm" />
          <p className="text-text-error typo-primary-2xl-light mb-xl">{t('common.generic_error')}</p>
          <p className="text-text-body typo-primary-xl">{t('activity.error')}</p>
        </div>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title={t('trades.trades_placed')}
        subtitle={t('activity.title')}
        backLinkUrl={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_TRADES.backPath
          ?.replace(':alias', alias.toString())
          .replace(':transactionId', transactionId.toString())}
      />

      {transaction && trades?.length ? (
        <div className="egw-container pt-0">
          <div className="inline-flex items-baseline justify-start gap-2xs">
            {transaction.amount ? (
              <>
                <div id="transaction_amount" className="flex items-baseline justify-start">
                  <div className="typo-primary-4xl-semibold text-text-header">
                    {Currency.onlyInteger.plusMinus(transaction.amount.amount)}
                  </div>
                  <div
                    className={cx('typo-primary-2xl-light', {
                      'text-header': transaction.amount.amount > 0,
                      'text-meta': transaction.amount.amount <= 0,
                    })}
                  >
                    {Currency.onlyCents.omitSign(transaction.amount.amount)}
                  </div>
                </div>
                <div
                  className={cx('font-bold', {
                    'text-header': transaction.amount.amount > 0,
                    'text-meta': transaction.amount.amount <= 0,
                  })}
                >
                  {transaction.amount.currencyCode}
                </div>
              </>
            ) : null}
          </div>
          <div className="mt-sm">
            {/* Trades list */}
            {trades.map((trade: InvestmentActivityTrade) => (
              <Link
                href={WEALTH_ROUTES.INVESTMENTS_ACCOUNT_TRADES_DETAILS.path
                  .replace(':alias', alias.toString())
                  .replace(':transactionId', transactionId.toString())
                  .replace(':tradeId', trade.tradeId.toString())}
                key={trade.tradeId}
              >
                <TradeField label={getLabel(trade)} value={getValue(trade)} />
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div className="egw-container">
          <div className="bg-bglight flex flex-col items-center mt-lg py-md px-lg gap-md">
            <p className="text-text-header text-center typo-primary-2xl-light">{t('activity.no_trades.title')}</p>
            <p className="text-text-body text-center">{t('activity.no_trades.description')}</p>
            <Button
              variant={ButtonVariant.Primary}
              onClick={() => {
                void navigate(
                  WEALTH_ROUTES.INVESTMENTS_ACCOUNT_TRADES.path
                    .replace(':alias', alias.toString())
                    .replace(':transactionId', transactionId.toString()),
                )
              }}
            >
              {t('activity.no_trades.cta_go_back')}
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
